import React, { useState, useEffect } from "react";

import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  Button,
  DateInput,
  PageHeader,
  SearchEmpty,
  DataTable,
} from "components";

import { usePermissions } from "hooks/permission";
import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";
import { useAppSelector } from "store/rootReducer";

import { colors } from "global/globalStyle";

import { IDataTableColumn } from "components/DataTable/interface";
import { DropdownGlobal } from "components/DropdownGlobal";

import { ReportsService } from "services/reportsService";
import {
  ILogOperation,
  ILogOperationType,
} from "services/reportsService/interfaces";

import { FORMS_RELATORIOS } from "utils/entities/permission";

import { ContainerLeftMenu } from "../../Administration/styles";
import { ReportMenu } from "../ReportMenu";
import { Container, ContainerBody, ContainerList } from "./styles";

export function LogOperations() {
  const ALL_OPERATIONS_OPTION = 0;
  const { t } = useTranslation();
  const today = new Date().toISOString().split("T")[0];
  const [listOfLogs, setListOfLogs] = useState<ILogOperation[]>([]);
  const [operations, setOperations] = useState<
    Array<{ id: number; name: string }>
  >([{ id: ALL_OPERATIONS_OPTION, name: t("common.all") }]);
  const [initialDate, setInitialDate] = useState<any>(today);
  const [loading, setLoading] = useState<boolean>(false);
  const [endDate, setEndDate] = useState<any>(today);

  const { user } = useAppSelector((state) => state.user);

  const dispatch = useDispatch();
  const { addToast } = useToast();

  const { read } = usePermissions(FORMS_RELATORIOS.LOG_OPERATION);

  const [dispositivo, setDispositivo] = useState("Todos");
  const [operacao, setOperacao] = useState(ALL_OPERATIONS_OPTION);
  const inputRefNew = React.useRef<HTMLInputElement>(null);

  const columns: IDataTableColumn[] = [
    {
      id: "userName",
      label: t("reports.columns.user"),
      width: "15%",
      align: "left",
    },
    {
      id: "operation",
      label: t("reports.columns.operation"),
      width: "15%",
      align: "center",
    },
    {
      id: "systemObject",
      label: t("reports.columns.object"),
      width: "15%",
      align: "center",
    },
    {
      id: "device",
      label: t("reports.columns.device"),
      width: "15%",
      align: "center",
    },
    {
      id: "ip",
      label: "IP",
      width: "15%",
      align: "center",
    },
    {
      id: "createIn",
      label: t("common.date"),
      width: "15%",
      align: "center",
      format: (value) => {
        if (!value) return "N/A";
        const date = dayjs(value);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <div>{date.format("DD/MM/YYYY")}</div>
            <div style={{ fontSize: "11px", color: colors.neutral_gray_40 }}>
              {date.format("HH:mm:ss")}
            </div>
          </div>
        );
      },
    },
  ];

  const options = [
    // {
    //   id: 0,
    //   name: "API",
    // },
    // {
    //   id: 2,
    //   name: t("reports.devices.mobile"),
    // },
    // {
    //   id: 1,
    //   name: "PC",
    // },
    {
      id: 3,
      name: t("common.all"),
    },
  ];
  useEffect(() => {
    if (inputRefNew.current) {
      inputRefNew.current.focus();
    }
  }, []);

  useEffect(() => {
    (async () => {
      const response = await ReportsService.getOperations();

      if (response.status && response.data?.data) {
        const mappedOperations = response.data.data.map(
          (operation: ILogOperationType) => ({
            id: operation.id,
            name: operation.description,
          }),
        );

        setOperations([
          { id: ALL_OPERATIONS_OPTION, name: t("common.all") },
          ...mappedOperations,
        ]);
        return;
      }

      addToast({
        type: "warning",
        title: t("common.warning"),
        description: response.message,
      });
    })();
  }, [addToast, t]);

  async function searchReportLogs() {
    if (!user?.customerId) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.needCustomerId"),
      });
      return;
    }
    if (!dispositivo) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.selectDevice"),
      });
      return;
    }

    if (initialDate > endDate) {
      addToast({
        type: "warning",
        title: t("common.warning"),
        description: t("reports.errors.invalidDateRange"),
      });
      return;
    }
    dispatch(showLoading());
    setLoading(true);
    const device = dispositivo === "Todos" ? null : dispositivo;
    const operation = operacao === ALL_OPERATIONS_OPTION ? null : operacao;

    setListOfLogs([]);
    const response = await ReportsService.getAllLogOperations(
      user?.customerId,
      initialDate + " 00:00",
      endDate + " 23:59",
      operation,
      device,
    );

    if (response?.status) {
      const responseData = response.data;
      const logs = Array.isArray(responseData)
        ? responseData
        : responseData?.data || [];

      setListOfLogs(logs);
    }

    dispatch(hideLoading());
    setLoading(false);
  }

  function onChangeDispositivo(value: any) {
    setDispositivo(value);
  }
  function onChangeOperacao(value: any) {
    setOperacao(value);
  }

  return (
    <Container>
      <ContainerLeftMenu>
        <ReportMenu />
      </ContainerLeftMenu>
      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <PageHeader
          quantity={listOfLogs.length > 0 ? listOfLogs.length : 0}
          id="header"
          label={t("reports.operationLogs")}
          background="secondary"
          addClick={undefined}
          disabled={false}
        />
        <ContainerBody>
          {read ? (
            <>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  marginTop: "25px",
                }}
              >
                <>
                  {/* <div style={{ marginLeft: 0 }}>
                    <DropdownGlobal
                      labelName={t("reports.columns.device")}
                      options={options}
                      setSelected={(value: any) => {
                        if (typeof value === "string")
                          onChangeDispositivo(value);
                      }}
                      defaultValue={"Todos"}
                      showDefaultValue
                      height="56px"
                      width="200px"
                      required={false}
                      withoutMarginBottom
                    />
                  </div> */}
                  <div style={{ marginLeft: 0 }}>
                    <DropdownGlobal
                      labelName={t("reports.columns.operation")}
                      options={operations}
                      defaultValue={t("common.all")}
                      showDefaultValue
                      setSelected={(value: any) => {
                        if (typeof value === "number") onChangeOperacao(value);
                      }}
                      height="56px"
                      width="200px"
                      required={false}
                      withoutMarginBottom
                    />
                  </div>
                  <div style={{ marginLeft: 20 }}>
                    <DateInput
                      label={t("reports.startDate")}
                      required={false}
                      value={initialDate}
                      setValue={setInitialDate}
                      disabled={false}
                      width={200}
                    />
                  </div>
                  <div style={{ marginLeft: 20 }}>
                    <DateInput
                      label={t("reports.endDate")}
                      required={false}
                      value={endDate}
                      setValue={setEndDate}
                      disabled={false}
                      width={200}
                    />
                  </div>
                </>
                <div style={{ marginLeft: 12, marginTop: 20 }}>
                  <Button onClick={() => searchReportLogs()}>
                    {t("reports.generateReport")}
                  </Button>
                </div>
              </div>
              <ContainerList>
                <DataTable
                  columns={columns}
                  rows={listOfLogs}
                  loading={loading}
                  emptyMessage={t("reports.logOperations.none")}
                  maxHeight="60vh"
                  stickyHeader
                  striped
                />
              </ContainerList>
            </>
          ) : (
            <SearchEmpty
              textMessage={t("common.noPermissionForProfile")}
              typeIcon={"avatar"}
              marginTop="30px"
            />
          )}
        </ContainerBody>
      </div>
    </Container>
  );
}
